import AnalyticsEventsCollectorInterface from "@modules/analytics_events_collector/AnalyticsEventsCollector/AnalyticsEventsCollectorInterface";
import { AnalyticsEventsFactory } from "@modules/analytics_events_collector/AnalyticsEventsCollector/AnalyticsEventsFactory";
import IntersectionObserverInjector from "@ui-utils/Injector/IntersectionObserverInjector";
import MutationObserverInjector from "@ui-utils/Injector/MutationObserverInjector";
import {
    parseIntDataAttr,
    parseStringDataAttr,
} from "@ui-utils/Injector/utils";

import { LoggerInterface } from "@interfaces/LoggerInterface";

import InstagramGalleryInstagramPageWidget from "./InstagramGalleryInstagramPageWidget";
import InstagramGalleryMainPageWidget from "./InstagramGalleryMainPageWidget";
import InstagramGalleryProductPageWidget from "./InstagramGalleryProductPageWidget";

const WIDGET_ID_DATA_ATTR = "widget";
const SHOW_STORIES_DATA_ATTR = "show-stories";
const TAGGED_ONLY_DATA_ATTR = "tagged";
const GALLERY_TITLE_DATA_ATTR = "gallery-title";
const HIGH_QUALITY_DATA_ATTR = "high-quality";
const GALLERY_ID_DATA_ATTR = "gallery_id";
const SHOP_INSTAGRAM_PAGE = "shop-instagram-page";
const PRODUCT_ID_DATA_ATTR = "product_id";

class LegacyInstagramGalleryWidgetInjector {
    private ioi: IntersectionObserverInjector;
    private placeholderSelector = ".ssw-instagram-widget";

    constructor(
        private readonly logger: LoggerInterface,
        private readonly eventsCollector: AnalyticsEventsCollectorInterface,
        private readonly analyticsEventsFactory: AnalyticsEventsFactory,
        private readonly moi: MutationObserverInjector
    ) {
        this.ioi = new IntersectionObserverInjector(
            IntersectionObserver,
            this.moi,
            this.placeholderSelector,
            {
                rootMargin: "0px 0px 50% 0px",
            }
        );
    }

    start(): void {
        this.logger?.debug("LegacyInstagramGalleryWidgetInjector.start", {
            placeholderSelector: this.placeholderSelector,
        });
        this.ioi.targets$.subscribe((placeholder) => {
            const innerPlaceholder =
                placeholder.querySelector<HTMLElement>(".ssw-page-loading");
            if (!innerPlaceholder) {
                this.logger?.error(
                    "InstagramGalleryWidgetInjector found placeholder but not found inner element",
                    {
                        placeholder,
                    }
                );
                return;
            }
            innerPlaceholder.innerHTML = "";
            this.logger?.debug(
                "LegacyInstagramGalleryWidgetInjector found placeholder",
                {
                    placeholder,
                }
            );
            const galleryId = parseIntDataAttr(
                placeholder,
                GALLERY_ID_DATA_ATTR
            );
            const widgetId = parseIntDataAttr(placeholder, WIDGET_ID_DATA_ATTR);
            const storiesEnabled = parseStringDataAttr(
                placeholder,
                SHOW_STORIES_DATA_ATTR
            );
            const taggedOnly = parseStringDataAttr(
                placeholder,
                TAGGED_ONLY_DATA_ATTR
            );
            const galleryTitle = parseStringDataAttr(
                placeholder,
                GALLERY_TITLE_DATA_ATTR
            );
            const highQualityEnabled = parseStringDataAttr(
                placeholder,
                HIGH_QUALITY_DATA_ATTR
            );
            const shopInstagramPage = parseStringDataAttr(
                placeholder,
                SHOP_INSTAGRAM_PAGE
            );
            const productId = parseIntDataAttr(
                placeholder,
                PRODUCT_ID_DATA_ATTR
            );
            if (!shopInstagramPage) {
                if (galleryId === null)
                    throw new Error(
                        `Placehoder does not have ${GALLERY_ID_DATA_ATTR} attribute.`
                    );
                if (widgetId === null)
                    throw new Error(
                        `Placehoder does not have ${WIDGET_ID_DATA_ATTR} attribute.`
                    );
            }
            const WidgetCls =
                galleryId !== null && productId === null
                    ? InstagramGalleryMainPageWidget
                    : galleryId !== null && productId !== null
                    ? InstagramGalleryProductPageWidget
                    : shopInstagramPage
                    ? InstagramGalleryInstagramPageWidget
                    : undefined;
            if (!WidgetCls) {
                throw new Error(
                    "Incorrect data-attributes set in placeholder. We cant detect with widget need use"
                );
            }
            this.eventsCollector.pushEvent(
                this.analyticsEventsFactory.createWidgetInjected(WidgetCls.name)
            );
            const widget = new WidgetCls(
                {
                    galleryId: galleryId,
                    galleryLayout: widgetId || 0,
                    isInstaPage: !!shopInstagramPage,
                    productId: productId,
                    customSettings: {
                        storiesEnabled: !!storiesEnabled,
                        filter: taggedOnly ? "only_tagged" : "all",
                        galleryTitle: galleryTitle,
                        highQualityEnabled: !!highQualityEnabled,
                        forceDisabledStories: false,
                    },
                    target: innerPlaceholder,
                },
                this.logger
            );
            widget.init();
        });

        this.ioi.start();
    }
}

export default LegacyInstagramGalleryWidgetInjector;
